import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertTriangle,
  Briefcase,
  Building2,
  CalendarRange,
  ChevronLeft,
  ClipboardList,
  ExternalLink,
  FileText,
  History,
  Loader2,
  MessageSquare,
  Receipt,
  Timer,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { formatCents } from '../lib/money'
import ExecutionPanel from '../components/cases/ExecutionPanel'
import MilestonesPanel from '../components/billing/MilestonesPanel'
import OpportunityRecurringPanel from '../components/billing/OpportunityRecurringPanel'
import RenewalAlert from '../components/billing/RenewalAlert'
import CaseTimePanel from '../components/cases/CaseTimePanel'
import DocumentsPanel from '../components/cases/DocumentsPanel'
import SentEmailsPanel from '../components/comms/SentEmailsPanel'
import TimelinePanel from '../components/cases/TimelinePanel'
import {
  type ExecutionPhase,
  type MilestoneDto,
  type MilestoneListResponse,
  type OpportunityDetail,
} from '@toga/shared'

// ---------------------------------------------------------------------------
// ProjectDetailPage — vista de EJECUCIÓN de un proyecto.
//
// "Proyecto" ≡ Opportunity con stage='GANADA' y executionPhase != null.
// Reutilizamos el endpoint GET /opportunities/:id; aquí filtramos para
// renderizar solo si realmente es un proyecto activo. Si no, mostramos un
// 404 amigable que lleva a la ficha comercial de la oportunidad.
// ---------------------------------------------------------------------------

type TabId = 'roadmap' | 'milestones' | 'time' | 'docs' | 'comms' | 'historico'

const ALL_TABS: { id: TabId; labelKey: string; icon: typeof FileText }[] = [
  { id: 'roadmap', labelKey: 'tabs.roadmap', icon: ClipboardList },
  { id: 'milestones', labelKey: 'tabs.milestones', icon: Receipt },
  { id: 'time', labelKey: 'tabs.time', icon: Timer },
  { id: 'docs', labelKey: 'tabs.docs', icon: FileText },
  { id: 'comms', labelKey: 'tabs.comms', icon: MessageSquare },
  { id: 'historico', labelKey: 'tabs.historico', icon: History },
]

const PHASE_BADGE: Record<ExecutionPhase, string> = {
  ONBOARDING: 'bg-amber-500/15 text-amber-700 dark:text-amber-200 border-amber-500/40',
  EJECUCION: 'bg-toga-500/15 text-toga-700 dark:text-toga-200 border-toga-500/30',
  FINALIZADO: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
  SOPORTE: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/40',
}

function formatPct(fraction: number): string {
  return `${(fraction * 100).toLocaleString(currentLocaleTag(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`
}

export default function ProjectDetailPage() {
  const { t } = useTranslation('projects')
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<OpportunityDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabId>('roadmap')
  // Hitos para los KPIs laterales (cobrados / pendientes). Cargados aparte del
  // panel para no tener que esperar a abrir la tab. Si falla, los KPIs quedan
  // como "—" pero la página sigue funcionando.
  const [milestones, setMilestones] = useState<MilestoneDto[]>([])

  const reload = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const { opportunity } = await api.get<{ opportunity: OpportunityDetail }>(
        `/opportunities/${id}`,
      )
      setData(opportunity)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError(t('errors.notFound'))
      } else {
        setError(err instanceof Error ? err.message : t('errors.loadFailed'))
      }
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    void reload()
  }, [reload])

  // Carga ligera de hitos del proyecto para KPIs (cobrados / pendientes).
  useEffect(() => {
    if (!data?.id) return
    let cancelled = false
    void (async () => {
      try {
        const resp = await api.get<MilestoneListResponse>(
          `/milestones?opportunityId=${data.id}`,
        )
        if (!cancelled) setMilestones(resp.items)
      } catch {
        if (!cancelled) setMilestones([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [data?.id])

  const milestoneStats = useMemo(() => {
    // "FACTURADO" como estado del hito solo significa que se generó una factura,
    // pero esa factura puede seguir en BORRADOR (no emitida al cliente todavía)
    // o estar ANULADA (rectificada). Ninguna de las dos es dinero facturado de
    // verdad, así que la KPI "Facturado" cuenta solo facturas EMITIDAS vivas
    // (EMITIDA / PARCIAL / COBRADA) por su invoiceStatus, no por el estado del hito.
    const LIVE_ISSUED = new Set(['EMITIDA', 'PARCIAL', 'COBRADA'])
    const facturados = milestones.filter(
      (m) => m.invoiceStatus != null && LIVE_ISSUED.has(m.invoiceStatus),
    )
    const cobrados = milestones.filter((m) => m.invoiceStatus === 'COBRADA')
    const pendientes = milestones.filter((m) => m.status === 'PENDIENTE')
    const sum = (arr: typeof milestones) => arr.reduce((acc, m) => acc + m.amountCents, 0)
    return {
      facturadosCount: facturados.length,
      cobradosCount: cobrados.length,
      pendientesCount: pendientes.length,
      facturadosCents: sum(facturados),
      cobradosCents: sum(cobrados),
      pendientesCents: sum(pendientes),
    }
  }, [milestones])

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" /> {t('loading')}
      </div>
    )
  }

  // Caso "no es un proyecto activo": existe la oportunidad pero o no está
  // GANADA, o no tiene executionPhase, o ha fallado la carga. Mostramos un
  // 404 amigable con un enlace a la ficha comercial cuando existe.
  const isActiveProject =
    !!data &&
    (data.stage === 'GANADA' || data.stage === 'EN_INVERSION') &&
    data.executionPhase != null

  if (error || !data || !isActiveProject) {
    return (
      <div className="max-w-xl mx-auto pt-12 surface p-8 text-center">
        <AlertTriangle size={32} className="mx-auto text-amber-700 dark:text-amber-300 mb-3" />
        <h2 className="text-xl font-serif font-semibold t-primary mb-1">
          {t('unavailable.title')}
        </h2>
        <p className="t-muted text-sm mb-4">
          {error ?? t('unavailable.notActive')}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Link
            to="/projects"
            className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-300 hover:underline"
          >
            <ChevronLeft size={14} /> {t('backToProjects')}
          </Link>
          {data && (
            <Link
              to={`/opportunities/${data.id}`}
              className="inline-flex items-center gap-1.5 text-sm text-toga-700 dark:text-toga-300 hover:underline"
            >
              <ExternalLink size={14} /> {t('viewOpportunity')}
            </Link>
          )}
        </div>
      </div>
    )
  }

  // A partir de aquí `data` está garantizado y es proyecto activo.
  // executionPhase != null lo aseguramos con isActiveProject.
  const phase = data.executionPhase as ExecutionPhase
  const projectValueCents =
    data.estimatedValueCents != null && data.estimatedValueCents > 0
      ? data.estimatedValueCents
      : data.profitability.revenueCents

  // La tab de TIEMPO está disponible en todas las fases: el tiempo imputado
  // sigue siendo consultable cuando el proyecto pasa a SOPORTE o FINALIZADO
  // (antes desaparecía al salir de EJECUCIÓN y con él todo su histórico/coste).
  const tabs = ALL_TABS

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link
            to="/projects"
            className="text-xs t-faint hover:underline inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} /> {t('breadcrumb')}
          </Link>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="font-mono text-xs text-emerald-700 dark:text-emerald-300">{data.code}</span>
            <h2 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
              <Briefcase size={20} className="text-emerald-700 dark:text-emerald-300" />
              {data.title}
            </h2>
          </div>
          <div className="t-muted text-sm mt-1 flex items-center gap-2 flex-wrap">
            <Building2 size={14} />
            <Link to={`/clients/${data.clientId}`} className="hover:underline">
              {data.clientDisplayName}
            </Link>
            <span className="t-faint">·</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full border ${PHASE_BADGE[phase]}`}
            >
              {t('enums:ExecutionPhase.' + phase)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/opportunities/${data.id}`}
            className="px-3 py-2 rounded-lg text-sm border border-base hover:bg-hover t-secondary inline-flex items-center gap-1.5"
            title={t('viewPipeline.title')}
          >
            <ExternalLink size={14} /> {t('viewPipeline.label')}
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        <div className="space-y-4">
          {/* Tabs */}
          <div className="surface p-1 flex flex-wrap gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const active = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                    active
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border border-emerald-500/40'
                      : 't-secondary hover:bg-hover border border-transparent'
                  }`}
                >
                  <Icon size={14} /> {t(tab.labelKey)}
                </button>
              )
            })}
          </div>

          {activeTab === 'roadmap' && (
            <ExecutionPanel
              opportunityId={data.id}
              executionPhase={data.executionPhase}
              canWrite={data.canWrite}
              onPhaseChanged={reload}
            />
          )}
          {activeTab === 'milestones' && (
            <div className="space-y-4">
              <RenewalAlert opportunityId={data.id} />
              <MilestonesPanel
                clientId={data.clientId}
                opportunityId={data.id}
                canWrite={data.canWrite}
                isProject
              />
              {/* LOTE B — pagos recurrentes del proyecto junto a los hitos. */}
              <OpportunityRecurringPanel opportunityId={data.id} />
            </div>
          )}
          {activeTab === 'time' && (
            <CaseTimePanel
              opportunityId={data.id}
              canWrite={data.canWrite}
              opportunityCode={data.code}
            />
          )}
          {activeTab === 'docs' && (
            <DocumentsPanel
              opportunityId={data.id}
              canWrite={data.canWrite}
              onChanged={() => {}}
            />
          )}
          {activeTab === 'comms' && (
            <SentEmailsPanel
              clientId={data.clientId}
              opportunityId={data.id}
              refreshKey={0}
            />
          )}
          {activeTab === 'historico' && (
            <TimelinePanel opportunityId={data.id} canWrite={data.canWrite} />
          )}
        </div>

        <aside className="space-y-4">
          <div className="surface p-5 space-y-3">
            <h3 className="t-primary font-semibold flex items-center gap-1.5">
              <TrendingUp size={16} className="text-emerald-700 dark:text-emerald-300" />
              {t('kpis.title')}
            </h3>
            <KpiRow
              label={t('kpis.projectValue')}
              value={formatCents(projectValueCents)}
            />
            <KpiRow
              label={t('kpis.milestonesInvoiced')}
              value={`${milestoneStats.facturadosCount} · ${formatCents(milestoneStats.facturadosCents)}`}
            />
            <KpiRow
              label={t('kpis.milestonesPaid')}
              value={`${milestoneStats.cobradosCount} · ${formatCents(milestoneStats.cobradosCents)}`}
              accent="emerald"
            />
            <KpiRow
              label={t('kpis.milestonesPending')}
              value={`${milestoneStats.pendientesCount} · ${formatCents(milestoneStats.pendientesCents)}`}
              accent="amber"
            />
            <KpiRow
              label={t('kpis.currentMargin')}
              value={formatPct(data.profitability.grossMarginPct)}
              accent={
                data.profitability.grossMarginPct >= 0 ? 'emerald' : 'rose'
              }
              internal
            />
          </div>

          {/* Tarjeta de fase (recordatorio rápido) */}
          <div className="surface p-5 text-sm space-y-2">
            <h3 className="t-primary font-semibold flex items-center gap-1.5">
              <CalendarRange size={16} className="text-emerald-700 dark:text-emerald-300" />
              {t('status.title')}
            </h3>
            <div className="flex justify-between text-xs">
              <span className="t-muted">{t('status.phase')}</span>
              <span
                className={`px-2 py-0.5 rounded-full border ${PHASE_BADGE[phase]}`}
              >
                {t('enums:ExecutionPhase.' + phase)}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="t-muted">{t('status.opened')}</span>
              <span className="t-primary font-medium">
                {new Date(data.openedAt).toLocaleDateString(currentLocaleTag())}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="t-muted">{t('status.team')}</span>
              <span className="t-primary font-medium">{data.assignedCount}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="t-muted">{t('status.documents')}</span>
              <span className="t-primary font-medium">{data.documentsCount}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

function KpiRow({
  label,
  value,
  accent,
  internal,
}: {
  label: string
  value: string
  accent?: 'emerald' | 'amber' | 'rose'
  internal?: boolean
}) {
  const colorCls =
    accent === 'emerald'
      ? 'text-emerald-700 dark:text-emerald-300'
      : accent === 'amber'
        ? 'text-amber-700 dark:text-amber-300'
        : accent === 'rose'
          ? 'text-rose-700 dark:text-rose-300'
          : 't-primary'
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="t-muted inline-flex items-center gap-1.5">
        {internal && (
          <Wallet size={11} className="text-amber-700 dark:text-amber-300/70" aria-label="interno" />
        )}
        {label}
      </span>
      <span className={`font-mono font-semibold ${colorCls}`}>{value}</span>
    </div>
  )
}
